import { Observable, Subject } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken } from '@mitsubishi/shared/data-access';

import {
  ConfiguratorResource,
  EvaluationResult,
  RuleEngineAnswerRequest,
  RulesResponseDto,
} from '../models/unit-rule-engine-configuration.model';

/**
 * HTTP client for the generic configurator rule-engine endpoints exposed by the
 * backend under `/v1/applyme/{resource}/{id}/configuration/...`. The same service
 * powers the Unit, StudyUnit and EaUnit flows — the `resource` segment is the
 * only thing that differs at the URL level.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfiguratorRuleEngineService {
  private readonly apiServerUrl: string;
  private readonly ruleEngineBaseUrl: string;

  #configurationUpdate = new Subject<void>();

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.apiServerUrl = apiServerUrl;
    this.ruleEngineBaseUrl = apiServerUrl + '/rule-engine';
  }

  readonly configurationUpdated$ = this.#configurationUpdate.asObservable();

  emitConfigurationUpdate(): void {
    this.#configurationUpdate.next();
  }

  private resourceBaseUrl(resource: ConfiguratorResource): string {
    return `${this.apiServerUrl}/${resource}`;
  }

  getSession(
    resource: ConfiguratorResource,
    entityId: string,
    projectId: string
  ): Observable<EvaluationResult> {
    const params = new HttpParams().set('projectId', projectId);
    return this.http.get<EvaluationResult>(
      `${this.resourceBaseUrl(resource)}/${entityId}/configuration/session`,
      { params }
    );
  }

  setAnswer(
    resource: ConfiguratorResource,
    entityId: string,
    questionCode: string,
    answerCode: string,
    projectId: string
  ): Observable<EvaluationResult> {
    const body: RuleEngineAnswerRequest = { answerCode };
    const params = new HttpParams().set('projectId', projectId);
    return this.http.put<EvaluationResult>(
      `${this.resourceBaseUrl(resource)}/${entityId}/configuration/answers/${questionCode}`,
      body,
      { params }
    );
  }

  removeAnswer(
    resource: ConfiguratorResource,
    entityId: string,
    questionCode: string,
    answerCode: string,
    projectId: string
  ): Observable<EvaluationResult> {
    const params = new HttpParams().set('projectId', projectId);
    return this.http.delete<EvaluationResult>(
      `${this.resourceBaseUrl(resource)}/${entityId}/configuration/answers/${questionCode}/${answerCode}`,
      { params }
    );
  }

  listRules(
    productGroup: string,
    productId: string,
    modelId: string,
    priceListId: string
  ): Observable<RulesResponseDto> {
    const params = new HttpParams()
      .set('productGroup', productGroup)
      .set('productId', productId)
      .set('modelId', modelId)
      .set('priceListId', priceListId);
    // Response is proxied as raw JSON from the rule engine; the shape matches RulesResponseDto.
    return this.http.get<RulesResponseDto>(`${this.ruleEngineBaseUrl}/rules`, { params });
  }
}
